import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import Helmet from "react-helmet";
import { RouteComponentProps } from "react-router";
import { useState as useWorkflowState } from "../../../context/reports/workflow";
import { HeadBar } from "../../../components/Reports";
import {
  StyledBackgroundRow,
  StyledWrap,
} from "../../../components/Reports/Reports.styles";
import { composeSites } from "../../../components/Reports/Workflow/utils";
import { useWorkflowEffects } from "./hooks/useWorkflowEffects";
import { useChart } from "../../../components/Reports/Workflow/hooks/useChart";
import { WFSite } from "../../../components/Reports/Workflow";
import { MetricsByType } from "../../../context/reports/workflow/types";
import { useActions } from "../../../context/reports/workflow/useActions";
import { useTrackedActions } from "../../../components/Reports/Workflow/hooks/useTrackedActions";
import { WORKFLOW_PERSIST_KEY } from "../../../context/reports/workflow/constants";
import {
  updateIsMultiSiteRoute,
  selectActiveSite,
} from "../../../redux/modules/_BETA/selections";

interface MatchParams {
  siteId: string;
  teamId: string;
}

const mapStateToProps = (state: Insite.AppState) => ({
  selectedSites: state.betaSelections.selectedSites,
  userSites: state.userSites,
  activeSite: state.betaSelections.activeSite,
});

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    setMultiSite: (syncoId: string) => {
      dispatch(selectActiveSite(syncoId));
      dispatch(updateIsMultiSiteRoute(false));
    },
  };
}

type ConnectedProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = ConnectedProps & DispatchProps & RouteComponentProps<MatchParams>;

const WFSiteScene: React.FunctionComponent<Props> = ({
  selectedSites,
  userSites,
  activeSite,
  match,
  setMultiSite,
}) => {
  const state = useWorkflowState();
  const { bars, gridData } = useChart(match);
  const { setSites, setByType } = useActions();
  const { trackedTimeFrame } = useTrackedActions();
  const site: Insite.Site = userSites.sites[match.params.siteId];
  const siteName = site.label;

  /**
   * Effects for triggering when to request/update
   * data based on metricType and active sites
   */
  useWorkflowEffects();

  /**
   * Update sites + metrics if:
   * 1. sites aren't yet populated in state
   * 2. sites have changed since initial load
   */
  React.useEffect(() => {
    if (
      (state.sites.size === 0 && selectedSites.length > 0) ||
      selectedSites.length !== state.sites.size ||
      selectedSites.some((site: string) => state.sites.get(site) === undefined)
    ) {
      setSites(composeSites(selectedSites, userSites.sites));
    }
  }, [selectedSites]);

  /**
   * Ensure correct byType state is set when hitting route cold
   */
  React.useEffect(() => {
    setByType(MetricsByType.BySite);
    setMultiSite(site.syncoId);
  }, []);

  const subTitle =
    selectedSites.length === 1 ? "Workflow Performance" : undefined;

  return (
    <>
      <Helmet title="Performance - Workflow" />
      <StyledBackgroundRow>
        <HeadBar
          backTo={
            selectedSites.length > 1
              ? "/reports/performance/workflow"
              : undefined
          }
          subTitle={subTitle}
          groupName={siteName}
          groupPrefix={userSites.projects[site.projectId].project_name}
          defaultTimeFrame={state.timeFrameOption}
          onTimeFrameChange={trackedTimeFrame}
          persistKey={WORKFLOW_PERSIST_KEY}
        />
      </StyledBackgroundRow>
      <StyledWrap>
        <WFSite
          bars={bars}
          gridData={gridData}
          selectedSites={selectedSites}
          siteLabel={activeSite.label}
        />
      </StyledWrap>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(WFSiteScene);
